import { useState } from "react";
import { useDataProvider, useNotify, useRecordContext } from "ra-core";
import {
  Calendar,
  Check,
  CheckCircle2,
  Copy,
  FileText,
  ListTodo,
  Loader2,
  MessageSquare,
  Sparkles,
  TrendingUp,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Contact } from "../types";

export interface ActionItemDraft {
  id: string;
  title: string;
  type: string;
  due_days: number;
  priority: "High" | "Medium" | "Low";
  selected: boolean;
}

interface AiMeetingSummarizerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplySummary?: (summaryText: string) => void;
  contact?: Contact | null;
}

export function AiMeetingSummarizerModal({
  open,
  onOpenChange,
  onApplySummary,
  contact: propContact,
}: AiMeetingSummarizerModalProps) {
  const record = useRecordContext<Contact>();
  const currentContact = propContact || record || null;
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [rawText, setRawText] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [summaryMarkdown, setSummaryMarkdown] = useState("");
  const [sentiment, setSentiment] = useState<string>("");
  const [dealStageSignal, setDealStageSignal] = useState<string>("");
  const [actionItems, setActionItems] = useState<ActionItemDraft[]>([]);
  const [schedulingTasks, setSchedulingTasks] = useState(false);
  const [tasksScheduled, setTasksScheduled] = useState(false);

  const contactName = currentContact
    ? `${currentContact.first_name || ""} ${currentContact.last_name || ""}`.trim()
    : "";

  const handleInsertSample = () => {
    setRawText(
      `Had a 35m discovery call with ${contactName || "the team"}. They currently struggle with disjointed prospect notes and lack visibility when champions switch jobs between accounts. They have 4 SDRs and 2 Account Executives who spend 2 hours a day manually writing follow-up emails and updating their CRM.\n\nNext steps agreed:\n- Send updated enterprise proposal with volume pricing by Friday\n- Schedule a deep-dive technical demo with the sales leadership next Tuesday\n- Send over security & SOC2 compliance overview`
    );
  };

  const handleSummarize = async () => {
    if (!rawText.trim()) {
      notify("Please enter or paste your meeting notes / transcript first.", {
        type: "warning",
      });
      return;
    }

    setLoading(true);
    setTasksScheduled(false);
    try {
      const supabase = getSupabaseClient();
      const res = await supabase.functions.invoke("meeting-summarizer", {
        body: {
          raw_text: rawText,
          contact_id: currentContact?.id,
          contact_name: contactName || undefined,
          company_name: currentContact?.company_name || undefined,
        },
      });

      if (res.error) {
        throw new Error(res.error.message);
      }

      const data = res.data;
      if (!data?.success) {
        throw new Error(data?.error || "Failed to generate summary");
      }

      setSummaryMarkdown(data.summary || "");
      setSentiment(data.sentiment || "Positive");
      setDealStageSignal(data.deal_stage_signal || "Qualified");

      const items: ActionItemDraft[] = (data.action_items || []).map(
        (item: any, idx: number) => ({
          id: `item-${Date.now()}-${idx}`,
          title: item.title,
          type: item.type || "Follow-up",
          due_days: item.due_days || 2,
          priority: item.priority || "Medium",
          selected: true,
        })
      );
      setActionItems(items);

      notify("Meeting summarized and action items extracted!", {
        type: "success",
      });
    } catch (err: any) {
      console.error("Meeting summarizer error:", err);
      notify(`Error summarizing call: ${err.message}`, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCopySummary = async () => {
    if (!summaryMarkdown) return;
    await navigator.clipboard.writeText(summaryMarkdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    notify("Summary copied to clipboard", { type: "info" });
  };

  const handleApplyToNote = () => {
    if (!summaryMarkdown) return;
    if (onApplySummary) {
      onApplySummary(summaryMarkdown);
      notify("Inserted executive summary into note editor!", {
        type: "success",
      });
      onOpenChange(false);
    }
  };

  const handleScheduleTasks = async () => {
    const selectedItems = actionItems.filter((i) => i.selected);
    if (selectedItems.length === 0) {
      notify("No action items selected to schedule.", { type: "warning" });
      return;
    }

    if (!currentContact?.id) {
      notify(
        "Cannot schedule CRM tasks without an active contact. Copy the summary instead.",
        { type: "warning" }
      );
      return;
    }

    setSchedulingTasks(true);
    try {
      let createdCount = 0;
      for (const item of selectedItems) {
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + (item.due_days || 1));

        await dataProvider.create("tasks", {
          data: {
            contact_id: currentContact.id,
            type: item.type || "Follow-up",
            text: item.title,
            due_date: dueDate.toISOString(),
          },
        });
        createdCount++;
      }

      setTasksScheduled(true);
      notify(`Successfully scheduled ${createdCount} follow-up task(s) in CRM!`, {
        type: "success",
      });
    } catch (err: any) {
      console.error("Task scheduling error:", err);
      notify(`Failed to schedule tasks: ${err.message}`, { type: "error" });
    } finally {
      setSchedulingTasks(false);
    }
  };

  const toggleActionItem = (id: string) => {
    setActionItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, selected: !i.selected } : i))
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-6 overflow-hidden">
        <DialogHeader className="border-b pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 rounded-lg">
                <Sparkles className="size-5" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold flex items-center gap-2">
                  AI Meeting Note Summarizer & Action Extractor
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                  Paste raw conversation notes or call transcripts. Gemini 2.5 Flash
                  formats the executive summary and schedules follow-up tasks into CRM.
                </DialogDescription>
              </div>
            </div>
            {contactName && (
              <Badge variant="outline" className="text-xs">
                {contactName}
              </Badge>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4 pr-1">
          {/* Input section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <FileText className="size-3.5" />
                Raw Notes / Call Transcript
              </label>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs text-indigo-600 hover:text-indigo-700 p-0 hover:bg-transparent"
                onClick={handleInsertSample}
              >
                Insert Sample Notes
              </Button>
            </div>
            <Textarea
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              placeholder="Paste raw bullet points, phone call transcript, or meeting notes here..."
              rows={4}
              className="resize-none font-sans text-sm focus-visible:ring-indigo-500"
            />
            <div className="flex justify-between items-center text-xs text-muted-foreground">
              <span>{rawText.trim() ? `${rawText.trim().split(/\s+/).length} words` : "Empty"}</span>
              <Button
                onClick={handleSummarize}
                disabled={loading || !rawText.trim()}
                className="bg-indigo-600 hover:bg-indigo-500 text-white gap-2 h-8 px-4"
              >
                {loading ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin" />
                    Analyzing with Gemini...
                  </>
                ) : (
                  <>
                    <Sparkles className="size-3.5" />
                    Summarize & Extract Tasks
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Result view */}
          {summaryMarkdown && (
            <div className="mt-4 border rounded-xl bg-muted/20 p-4 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-3">
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-600 text-white font-medium text-xs">
                    Sentiment: {sentiment}
                  </Badge>
                  {dealStageSignal && (
                    <Badge variant="outline" className="text-xs border-indigo-200 text-indigo-700 dark:text-indigo-300">
                      <TrendingUp className="size-3 mr-1" />
                      Stage: {dealStageSignal}
                    </Badge>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs gap-1.5"
                    onClick={handleCopySummary}
                  >
                    {copied ? <Check className="size-3 text-emerald-600" /> : <Copy className="size-3" />}
                    {copied ? "Copied" : "Copy Markdown"}
                  </Button>
                  {onApplySummary && (
                    <Button
                      size="sm"
                      className="h-7 text-xs bg-indigo-600 hover:bg-indigo-500 text-white gap-1.5"
                      onClick={handleApplyToNote}
                    >
                      <CheckCircle2 className="size-3" />
                      Apply to Note Box
                    </Button>
                  )}
                </div>
              </div>

              <Tabs defaultValue="summary" className="w-full">
                <TabsList className="grid grid-cols-2 w-64 h-8 mb-3">
                  <TabsTrigger value="summary" className="text-xs flex items-center gap-1.5">
                    <FileText className="size-3" />
                    Executive Summary
                  </TabsTrigger>
                  <TabsTrigger value="actions" className="text-xs flex items-center gap-1.5">
                    <ListTodo className="size-3" />
                    Tasks ({actionItems.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="summary" className="mt-0">
                  <pre className="p-3 bg-card border rounded-lg text-xs font-mono whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto text-foreground">
                    {summaryMarkdown}
                  </pre>
                </TabsContent>

                <TabsContent value="actions" className="mt-0 space-y-3">
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {actionItems.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => toggleActionItem(item.id)}
                        className={`flex items-start gap-3 p-2.5 rounded-lg border text-xs cursor-pointer transition-colors ${
                          item.selected
                            ? "bg-indigo-50/50 border-indigo-200 dark:bg-indigo-950/20 dark:border-indigo-800"
                            : "bg-card opacity-60 border-border"
                        }`}
                      >
                        <Checkbox
                          checked={item.selected}
                          onCheckedChange={() => toggleActionItem(item.id)}
                          className="mt-0.5"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="secondary" className="text-[10px] uppercase font-semibold">
                              {item.type}
                            </Badge>
                            <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                              <Calendar className="size-3" />
                              Due in {item.due_days} day{item.due_days > 1 ? "s" : ""}
                            </span>
                            <span
                              className={`text-[10px] font-semibold ${
                                item.priority === "High"
                                  ? "text-rose-600"
                                  : "text-amber-600"
                              }`}
                            >
                              {item.priority} Priority
                            </span>
                          </div>
                          <p className="font-medium text-foreground">{item.title}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {currentContact?.id && (
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-xs text-muted-foreground">
                        {actionItems.filter((i) => i.selected).length} action items selected
                      </span>
                      <Button
                        size="sm"
                        disabled={schedulingTasks || tasksScheduled || actionItems.filter((i) => i.selected).length === 0}
                        onClick={handleScheduleTasks}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-8 gap-1.5"
                      >
                        {schedulingTasks ? (
                          <>
                            <Loader2 className="size-3.5 animate-spin" />
                            Scheduling...
                          </>
                        ) : tasksScheduled ? (
                          <>
                            <Check className="size-3.5" />
                            Tasks Added to CRM!
                          </>
                        ) : (
                          <>
                            <ListTodo className="size-3.5" />
                            Auto-Schedule in CRM Tasks
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-3 flex justify-between sm:justify-between items-center">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {summaryMarkdown && onApplySummary && (
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-500 text-white gap-2"
              onClick={handleApplyToNote}
            >
              <Sparkles className="size-3.5" />
              Done & Insert to Note
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AiSummarizeCallButton({
  onApplySummary,
  contact,
  className,
}: {
  onApplySummary?: (summaryText: string) => void;
  contact?: Contact | null;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 text-xs font-medium border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 hover:text-indigo-800 transition-colors shadow-2xs ${className || ""}`}
      >
        <Sparkles className="size-3.5 text-indigo-600" />
        <span>AI Summarize Call</span>
      </Button>

      <AiMeetingSummarizerModal
        open={open}
        onOpenChange={setOpen}
        onApplySummary={onApplySummary}
        contact={contact}
      />
    </>
  );
}
